import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BookingDetails,
  BookingResultType,
  RescheduleSeat,
} from '../../api/bookingApi.types';
import {
  BookingFailure,
  cancelBookingAsCandidate,
  fetchBooking,
  rescheduleBookingAsCandidate,
} from '../../api/bookingOperations';
import { BookingRefusalCode } from '../../api/bookingTransport';
import { SchedulingPageState } from '../../scheduling.types';
import {
  formatDayFull,
  formatSlotRange,
  getBrowserTimeZone,
} from '../../timezone';
import BookedState from '../BookedState/BookedState';
import StateMessage from '../StateMessage/StateMessage';
import ActionScreen, { ScreenNotice } from './ActionScreen';
import {
  blockedReasonKey,
  isStaleRefusal,
  refusalReasonKey,
} from './candidateActionCopy';
import ReschedulePickState from './ReschedulePickState';

type FlowStep =
  | { kind: 'booked' }
  | { kind: 'manage' }
  | { kind: 'cancelConfirm' }
  | { kind: 'cancelled' }
  | { kind: 'pick' }
  | { kind: 'rescheduleConfirm'; seat: RescheduleSeat }
  /** The read offered no other time at all. */
  | { kind: 'noOtherTimes' }
  /** Other times were offered, but the candidate can make none of them. */
  | { kind: 'askOtherTimes' }
  | { kind: 'asked' };

interface BookedFlowProps {
  booking: BookingDetails;
  token: string;
  timeZone: string;
  /** Moves focus to the booked heading, for a booking made by pressing Confirm. */
  focusHeading?: boolean;
  /**
   * An answer that ends the page rather than the step: the token stopped
   * resolving, or a newer link replaced this one.
   */
  onTerminal: (state: SchedulingPageState) => void;
  /** Re-reads the link from scratch, for a booking that changed under the page. */
  onReload: () => void;
  announce: (text: string) => void;
}

/**
 * The booked page and the flows a candidate can start from it (BL-1593):
 * cancel, move to another of the times they were offered, or ask the hiring
 * team for new ones. Each flow confirms before it acts, and every answer the
 * two operations can give lands on copy the candidate can act on.
 *
 * The booking is held here rather than taken from the page, because a move
 * replaces it: the page then shows the new interview read back from the
 * server, the same page a reopened link gives.
 */
const BookedFlow: FC<BookedFlowProps> = ({
  booking: initialBooking,
  token,
  timeZone: initialTimeZone,
  focusHeading = false,
  onTerminal,
  onReload,
  announce,
}) => {
  const { t } = useTranslation();
  const [booking, setBooking] = useState(initialBooking);
  const [timeZone, setTimeZone] = useState(initialTimeZone);
  const [step, setStep] = useState<FlowStep>({ kind: 'booked' });
  /** Whether the booked page was reached by pressing a control, so it takes focus. */
  const [returned, setReturned] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<ScreenNotice | null>(null);

  const when = (slot: { startsAt: string; endsAt: string }) => ({
    day: formatDayFull(slot.startsAt, timeZone),
    range: formatSlotRange(slot, timeZone),
  });

  const goTo = (next: FlowStep) => {
    setNotice(null);
    setStep(next);
  };

  const backToBooked = () => {
    setReturned(true);
    goTo({ kind: 'booked' });
  };

  const reloadNotice = (key: string): ScreenNotice => ({
    text: t(key),
    action: { label: t('publicScheduling.reload'), onClick: onReload },
  });

  /** A refusal from the closed list; the stale ones offer a reload. */
  const applyRefusal = (reason: string) => {
    const key = refusalReasonKey(reason);
    setNotice(isStaleRefusal(reason) ? reloadNotice(key) : { text: t(key) });
  };

  /** A transport or contract failure. Only not-found ends the page. */
  const applyFailure = (failure: BookingFailure, fallbackKey: string) => {
    if (
      failure.kind === 'refused' &&
      failure.code === BookingRefusalCode.NOT_FOUND
    ) {
      onTerminal({ kind: 'notFound' });
      return;
    }
    setNotice({ text: t(fallbackKey) });
  };

  /**
   * Reads the booking again, for a move that went through or a time that
   * was taken. A booking that is gone hands the page back to the link's
   * read flow, which says what it can.
   */
  const refreshBooking = async (): Promise<BookingDetails | null> => {
    const call = await fetchBooking(token);
    if (!call.ok) {
      return null;
    }
    switch (call.result.__typename) {
      case BookingResultType.BOOKING_DETAILS:
        setBooking(call.result);
        return call.result;
      case BookingResultType.LINK_REPLACED_STATE:
        onTerminal({ kind: 'replaced' });
        return null;
      default:
        onReload();
        return null;
    }
  };

  const cancel = async () => {
    setBusy(true);
    try {
      const call = await cancelBookingAsCandidate(token, booking.bookingId);
      if (!call.ok) {
        applyFailure(call.failure, 'publicScheduling.cancelFailedNotice');
        return;
      }
      switch (call.result.__typename) {
        case BookingResultType.BOOKING_CANCELLED_BY_CANDIDATE:
          goTo({ kind: 'cancelled' });
          announce(t('publicScheduling.announceCancelled'));
          return;
        case BookingResultType.CANDIDATE_ACTION_NOT_ALLOWED:
          applyRefusal(call.result.reason);
          return;
        case BookingResultType.LINK_REPLACED_STATE:
          onTerminal({ kind: 'replaced' });
          return;
      }
    } finally {
      setBusy(false);
    }
  };

  const startReschedule = () =>
    goTo({ kind: booking.otherTimes.length > 0 ? 'pick' : 'noOtherTimes' });

  /**
   * Moves to `seat`. `timezone` is the zone the candidate picked the time
   * in and `candidateTimezone` the zone their device reports, as when they
   * booked.
   */
  const reschedule = async (seat: RescheduleSeat) => {
    setBusy(true);
    try {
      const call = await rescheduleBookingAsCandidate(token, {
        bookingId: booking.bookingId,
        seatId: seat.seatId,
        timezone: timeZone,
        candidateTimezone: getBrowserTimeZone(),
      });
      if (!call.ok) {
        applyFailure(call.failure, 'publicScheduling.rescheduleFailedNotice');
        return;
      }
      switch (call.result.__typename) {
        case BookingResultType.BOOKING_CONFIRMED: {
          const moved = await refreshBooking();
          if (moved) {
            backToBooked();
            announce(t('publicScheduling.announceRescheduled', when(moved)));
          } else {
            setNotice(
              reloadNotice('publicScheduling.rescheduledReadFailedNotice')
            );
          }
          return;
        }
        case BookingResultType.NO_OTHER_TIMES_STATE:
          goTo({ kind: 'asked' });
          announce(t('publicScheduling.announceAsked'));
          return;
        case BookingResultType.CANDIDATE_ACTION_NOT_ALLOWED:
          applyRefusal(call.result.reason);
          return;
        case BookingResultType.SEAT_UNAVAILABLE: {
          const fresh = await refreshBooking();
          setStep({
            kind:
              fresh && fresh.otherTimes.length === 0 ? 'noOtherTimes' : 'pick',
          });
          setNotice({ text: t('publicScheduling.seatTakenNotice') });
          return;
        }
        case BookingResultType.INVALID_TIMEZONE:
          setStep({ kind: 'pick' });
          setNotice({ text: t('publicScheduling.timezoneRejectedNotice') });
          return;
        case BookingResultType.LINK_REPLACED_STATE:
          onTerminal({ kind: 'replaced' });
          return;
      }
    } finally {
      setBusy(false);
    }
  };

  /**
   * Asks the hiring team for new times, which leaves the interview as it
   * is. The same ask whether the read offered no other time or the
   * candidate can make none of those it did.
   */
  const ask = async () => {
    setBusy(true);
    try {
      const call = await rescheduleBookingAsCandidate(token, {
        bookingId: booking.bookingId,
      });
      if (!call.ok) {
        applyFailure(call.failure, 'publicScheduling.askFailedNotice');
        return;
      }
      switch (call.result.__typename) {
        case BookingResultType.NO_OTHER_TIMES_STATE:
          goTo({ kind: 'asked' });
          announce(t('publicScheduling.announceAsked'));
          return;
        case BookingResultType.CANDIDATE_ACTION_NOT_ALLOWED:
          applyRefusal(call.result.reason);
          return;
        case BookingResultType.LINK_REPLACED_STATE:
          onTerminal({ kind: 'replaced' });
          return;
        default:
          // An ask names no seat, so the server has no seat or zone to
          // refuse and nothing to move; anything else is an answer this
          // page did not expect.
          setNotice({ text: t('publicScheduling.askFailedNotice') });
          return;
      }
    } finally {
      setBusy(false);
    }
  };

  switch (step.kind) {
    case 'manage': {
      const rescheduleBlocked = !booking.canReschedule
        ? blockedReasonKey(booking.blockedReason)
        : null;
      return (
        <ActionScreen
          title={t('publicScheduling.manageTitle')}
          body={[
            t('publicScheduling.manageSubline', when(booking)),
            rescheduleBlocked && t(rescheduleBlocked),
          ]
            .filter(Boolean)
            .join(' ')}
          notice={notice}
          busy={busy}
          actions={[
            ...(booking.canReschedule
              ? [
                  {
                    label: t(
                      booking.otherTimes.length > 0
                        ? 'publicScheduling.chooseAnotherTime'
                        : 'publicScheduling.askForOtherTimes'
                    ),
                    variant: 'primary' as const,
                    onClick: startReschedule,
                  },
                ]
              : []),
            ...(booking.canCancel
              ? [
                  {
                    label: t('publicScheduling.cancelInterview'),
                    variant: 'negative' as const,
                    onClick: () => goTo({ kind: 'cancelConfirm' }),
                  },
                ]
              : []),
            {
              label: t('publicScheduling.keepThisInterview'),
              variant: 'tertiary' as const,
              onClick: backToBooked,
            },
          ]}
        />
      );
    }
    case 'cancelConfirm':
      return (
        <ActionScreen
          title={t('publicScheduling.cancelConfirmTitle')}
          body={t('publicScheduling.cancelConfirmBody', when(booking))}
          notice={notice}
          busy={busy}
          actions={[
            {
              label: t('publicScheduling.confirmCancel'),
              variant: 'negativePrimary',
              loading: busy,
              onClick: cancel,
            },
            {
              label: t('publicScheduling.keepThisInterview'),
              variant: 'tertiary',
              onClick: backToBooked,
            },
          ]}
        />
      );
    case 'cancelled':
      return (
        <StateMessage
          title={t('publicScheduling.cancelledTitle')}
          body={t('publicScheduling.cancelledBody')}
          focusTitle
        />
      );
    case 'pick':
      return (
        <ReschedulePickState
          booking={booking}
          timeZone={timeZone}
          onTimeZoneChange={setTimeZone}
          notice={notice}
          onContinue={seat => goTo({ kind: 'rescheduleConfirm', seat })}
          onCantDoAny={() => goTo({ kind: 'askOtherTimes' })}
          onBack={backToBooked}
        />
      );
    case 'rescheduleConfirm': {
      const from = when(booking);
      const to = when(step.seat);
      return (
        <ActionScreen
          title={t('publicScheduling.rescheduleConfirmTitle')}
          body={t('publicScheduling.rescheduleConfirmBody', {
            fromDay: from.day,
            fromRange: from.range,
            toDay: to.day,
            toRange: to.range,
          })}
          notice={notice}
          busy={busy}
          actions={[
            {
              label: t('publicScheduling.confirmNewTime'),
              variant: 'primary',
              loading: busy,
              onClick: () => reschedule(step.seat),
            },
            {
              label: t('publicScheduling.chooseDifferentTime'),
              variant: 'tertiary',
              onClick: () => goTo({ kind: 'pick' }),
            },
          ]}
        />
      );
    }
    case 'noOtherTimes':
      return (
        <ActionScreen
          title={t('publicScheduling.noOtherTimesTitle')}
          body={t('publicScheduling.noOtherTimesBody', when(booking))}
          notice={notice}
          busy={busy}
          actions={[
            {
              label: t('publicScheduling.askHiringTeam'),
              variant: 'primary',
              loading: busy,
              onClick: ask,
            },
            {
              label: t('publicScheduling.keepThisInterview'),
              variant: 'tertiary',
              onClick: backToBooked,
            },
          ]}
        />
      );
    case 'askOtherTimes':
      return (
        <ActionScreen
          title={t('publicScheduling.askOtherTimesTitle')}
          body={t('publicScheduling.askOtherTimesBody', when(booking))}
          notice={notice}
          busy={busy}
          actions={[
            {
              label: t('publicScheduling.askHiringTeam'),
              variant: 'primary',
              loading: busy,
              onClick: ask,
            },
            {
              label: t('publicScheduling.lookAtTimesAgain'),
              variant: 'tertiary',
              onClick: () => goTo({ kind: 'pick' }),
            },
          ]}
        />
      );
    case 'asked':
      return (
        <StateMessage
          title={t('publicScheduling.askedTitle')}
          body={t('publicScheduling.askedBody', when(booking))}
          action={{
            label: t('publicScheduling.backToInterview'),
            onClick: backToBooked,
          }}
          focusTitle
        />
      );
    default:
      return (
        <BookedState
          booking={booking}
          token={token}
          timeZone={timeZone}
          focusHeading={focusHeading || returned}
          onManage={() => goTo({ kind: 'manage' })}
        />
      );
  }
};

export default BookedFlow;
